import { Controller } from "@hotwired/stimulus"
import { createPopper } from "@popperjs/core"

// Drives DepozenUi::TooltipComponent: Popper positioning, show/hide delays and a CSS fade.
// The bubble is moved to <body> on first show so overflow:hidden ancestors never clip it.
export default class extends Controller {
  static targets = ["bubble", "arrow"]
  static values = {
    placement: { type: String, default: "top" },
    trigger: { type: Array, default: ["hover", "focus"] },
    showDelay: { type: Number, default: 150 },
    hideDelay: { type: Number, default: 100 },
    fadeDuration: { type: Number, default: 150 },
    offset: { type: Number, default: 8 },
    interactive: { type: Boolean, default: false },
    flip: { type: Boolean, default: true },
    open: { type: Boolean, default: false }
  }

  connect() {
    // Captured now: once moved to <body> the bubble is out of this controller's scope.
    this.bubble = this.bubbleTarget
    this.arrow = this.hasArrowTarget ? this.arrowTarget : null
    this.bubble.style.setProperty("--depozen-ui-tooltip-fade", `${this.fadeDurationValue}ms`)
    this.visible = false
    this.handlers = []
    this.bindTriggers()
    if (this.openValue) this.show()
  }

  disconnect() {
    this.clearTimers()
    this.handlers.forEach(([target, type, handler]) => target.removeEventListener(type, handler))
    this.handlers = []
    this.popper?.destroy()
    this.popper = null
    this.bubble?.remove()
  }

  // Manual API: data-action="click->depozen-ui-tooltip#toggle", or openValue from the outside.
  openValueChanged(open) {
    if (!this.bubble) return
    open ? this.show() : this.hide()
  }

  show() {
    this.clearTimers()
    if (this.visible) return

    this.visible = true
    if (this.bubble.parentElement !== document.body) document.body.appendChild(this.bubble)
    this.bubble.setAttribute("data-show", "")
    this.ensurePopper()
    this.popper.update().then(() => {
      if (this.visible) requestAnimationFrame(() => this.bubble.classList.add("is-visible"))
    })
    this.dispatch("shown")
  }

  hide() {
    this.clearTimers()
    if (!this.visible) return

    this.visible = false
    this.bubble.classList.remove("is-visible")
    this.fadeTimer = setTimeout(() => {
      if (this.visible) return
      this.bubble.removeAttribute("data-show")
      this.setPopperListeners(false)
    }, this.fadeDurationValue)
    this.dispatch("hidden")
  }

  toggle() {
    this.visible ? this.hide() : this.show()
  }

  scheduleShow(delay = this.showDelayValue) {
    this.clearTimers()
    this.showTimer = setTimeout(() => this.show(), delay)
  }

  scheduleHide(delay = this.hideDelayValue) {
    this.clearTimers()
    this.hideTimer = setTimeout(() => this.hide(), delay)
  }

  clearTimers() {
    clearTimeout(this.showTimer)
    clearTimeout(this.hideTimer)
    clearTimeout(this.fadeTimer)
  }

  bindTriggers() {
    const triggers = this.triggerValue

    if (triggers.includes("hover")) {
      this.on(this.element, "mouseenter", () => this.scheduleShow())
      this.on(this.element, "mouseleave", () => this.scheduleHide())
      if (this.interactiveValue) {
        this.on(this.bubble, "mouseenter", () => this.clearTimers())
        this.on(this.bubble, "mouseleave", () => this.scheduleHide())
      }
    }

    if (triggers.includes("focus")) {
      this.on(this.element, "focusin", () => this.scheduleShow(0))
      this.on(this.element, "focusout", (event) => {
        if (!this.bubble.contains(event.relatedTarget)) this.scheduleHide(0)
      })
    }

    if (triggers.includes("click")) {
      this.on(this.element, "click", (event) => {
        if (!this.bubble.contains(event.target)) this.toggle()
      })
      this.on(document, "click", (event) => {
        if (this.visible && !this.element.contains(event.target) && !this.bubble.contains(event.target)) this.hide()
      })
    }

    this.on(document, "keydown", (event) => {
      if (event.key === "Escape" && this.visible) this.hide()
    })
  }

  on(target, type, handler) {
    target.addEventListener(type, handler)
    this.handlers.push([target, type, handler])
  }

  ensurePopper() {
    if (this.popper) {
      this.setPopperListeners(true)
      return
    }

    const modifiers = [
      { name: "offset", options: { offset: [0, this.offsetValue] } },
      { name: "flip", enabled: this.flipValue },
      { name: "preventOverflow", options: { padding: 8 } }
    ]
    if (this.arrow) modifiers.push({ name: "arrow", options: { element: this.arrow, padding: 6 } })

    this.popper = createPopper(this.element, this.bubble, {
      placement: this.placementValue,
      strategy: "fixed",
      modifiers
    })
  }

  // Popper's scroll/resize listeners only while shown (its own recommendation for hidden tooltips).
  setPopperListeners(enabled) {
    this.popper?.setOptions((options) => ({
      ...options,
      modifiers: [...options.modifiers.filter((m) => m.name !== "eventListeners"), { name: "eventListeners", enabled }]
    }))
  }
}
